// client/src/scenes/CameraPermissionScene.js

import Phaser from 'phaser';
import CameraManager from '../utils/cameraAccess';

class CameraPermissionScene extends Phaser.Scene {
    constructor() {
        super({ key: 'CameraPermissionScene' });
        this.cameraManager = null;
        this.permissionGranted = false;
        this.permissionRequested = false;
        this.videoElement = null;
    }

    preload() {
        // We'll use basic shapes instead of images for now
        console.log('CameraPermissionScene preload');
    }

    create() {
        console.log('CameraPermissionScene create');

        // Add background
        this.add.rectangle(400, 300, 800, 600, 0x111111);

        // Add title
        this.add.text(400, 100, 'Camera Access Required', {
            fontFamily: 'Arial',
            fontSize: 32,
            color: '#ffffff'
        }).setOrigin(0.5);

        // Add camera icon (simple circle placeholder)
        this.cameraIcon = this.add.circle(400, 220, 50, 0x4CAF50);

        // Add explanation text
        const explanation = [
            "This game uses your camera to track hand gestures.",
            "Your camera feed is processed locally and never sent to any server.",
            "Please allow camera access when prompted to play the game."
        ];

        let yPos = 320;
        explanation.forEach(line => {
            this.add.text(400, yPos, line, {
                fontFamily: 'Arial',
                fontSize: 18,
                color: '#ffffff',
                align: 'center'
            }).setOrigin(0.5);
            yPos += 30;
        });

        // Add status text
        this.statusText = this.add.text(400, 430, 'Click the button below to continue', {
            fontFamily: 'Arial',
            fontSize: 18,
            color: '#ffff00'
        }).setOrigin(0.5);

        // Add status icon (will update based on permission)
        this.statusIcon = this.add.circle(400, 480, 15, 0xcccccc);
        this.statusIcon.setVisible(false);

        // Add continue button
        this.createContinueButton();

        // Add back button
        this.createBackButton();

        // Initialize camera manager
        this.cameraManager = new CameraManager();
    }

    createContinueButton() {
        // Create button background
        this.continueButton = this.add.rectangle(400, 520, 220, 50, 0x4CAF50);
        this.continueButton.setInteractive({ useHandCursor: true });

        // Add button text
        this.continueText = this.add.text(400, 520, 'Request Camera Access', {
            fontFamily: 'Arial',
            fontSize: 18,
            color: '#ffffff'
        }).setOrigin(0.5);

        // Add button events
        this.continueButton.on('pointerover', () => {
            this.continueButton.fillColor = 0x66BB6A;
        });

        this.continueButton.on('pointerout', () => {
            this.continueButton.fillColor = 0x4CAF50;
        });

        this.continueButton.on('pointerdown', () => {
            this.continueButton.fillColor = 0x388E3C;
        });

        this.continueButton.on('pointerup', () => {
            if (!this.permissionRequested) {
                this.requestCameraPermission();
            } else if (this.permissionGranted) {
                this.startGame();
            } else {
                this.requestCameraPermission();
            }
        });
    }

    createBackButton() {
        // Create button background
        this.backButton = this.add.rectangle(100, 520, 160, 50, 0xF44336);
        this.backButton.setInteractive({ useHandCursor: true });

        // Add button text
        this.add.text(100, 520, 'Back to Menu', {
            fontFamily: 'Arial',
            fontSize: 18,
            color: '#ffffff'
        }).setOrigin(0.5);

        // Add button events
        this.backButton.on('pointerover', () => {
            this.backButton.fillColor = 0xEF5350;
        });

        this.backButton.on('pointerout', () => {
            this.backButton.fillColor = 0xF44336;
        });

        this.backButton.on('pointerdown', () => {
            this.backButton.fillColor = 0xD32F2F;
        });

        this.backButton.on('pointerup', () => {
            // Clean up
            this.cleanupVideoElement();

            // Go back to main menu
            this.scene.start('MainMenuScene');
        });
    }

    async requestCameraPermission() {
        this.permissionRequested = true;
        this.statusText.setText('Requesting camera access...');
        this.continueText.setText('Requesting...');
        this.continueButton.disableInteractive();

        // Animate camera icon
        this.tweens.add({
            targets: this.cameraIcon,
            scaleX: 0.9,
            scaleY: 0.9,
            duration: 300,
            yoyo: true,
            repeat: -1
        });

        // Create video element if it doesn't exist
        if (!this.videoElement) {
            this.videoElement = document.createElement('video');
            this.videoElement.setAttribute('autoplay', true);
            this.videoElement.setAttribute('playsinline', true);
            this.videoElement.style.display = 'none';
            document.body.appendChild(this.videoElement);
        }

        // Request camera access
        const success = await this.cameraManager.initialize(this.videoElement);

        // Stop animation
        this.tweens.killTweensOf(this.cameraIcon);
        this.cameraIcon.setScale(1);

        if (success) {
            this.onPermissionGranted();
        } else {
            this.onPermissionDenied();
        }
    }

    onPermissionGranted() {
        this.permissionGranted = true;

        // Update UI
        this.statusText.setText('Camera access granted! Click Continue to start the game.');
        this.statusText.setColor('#00ff00');

        this.statusIcon.fillColor = 0x00ff00;
        this.statusIcon.setVisible(true);

        this.continueText.setText('Continue to Game');
        this.continueButton.setInteractive({ useHandCursor: true });

        // Pulse the continue button to draw attention
        this.tweens.add({
            targets: this.continueButton,
            scaleX: 1.05,
            scaleY: 1.05,
            duration: 500,
            yoyo: true,
            repeat: 3
        });
    }

    onPermissionDenied() {
        // Update UI
        this.statusText.setText('Camera access denied. Please allow camera access in your browser settings.');
        this.statusText.setColor('#ff0000');

        this.statusIcon.fillColor = 0xff0000;
        this.statusIcon.setVisible(true);

        this.continueText.setText('Try Again');
        this.continueButton.setInteractive({ useHandCursor: true });

        // Show help text with instructions
        const helpText = [
            "To enable camera access:",
            "1. Click the camera/lock icon in your browser's address bar",
            "2. Select 'Allow' for camera access",
            "3. Click 'Try Again' below"
        ];

        let yPos = 350;
        helpText.forEach(line => {
            this.add.text(400, yPos, line, {
                fontFamily: 'Arial',
                fontSize: 14,
                color: '#ffffff',
                align: 'center'
            }).setOrigin(0.5);
            yPos += 20;
        });
    }

    startGame() {
        // Clean up the video element (will be recreated in gameplay scene)
        this.cleanupVideoElement();

        // Go to gameplay scene
        this.scene.start('GameplayScene');
    }

    cleanupVideoElement() {
        if (this.videoElement && document.body.contains(this.videoElement)) {
            // Stop camera before removing
            if (this.cameraManager) {
                this.cameraManager.stopCamera();
            }

            // Remove video element
            document.body.removeChild(this.videoElement);
            this.videoElement = null;
        }
    }

    update() {
        // Any per-frame updates can go here
    }
}

export default CameraPermissionScene;